import { useEffect, useState, type FunctionComponent } from "react";
import { SheetFooter, SheetTitle } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import CheckIcon from "../../../../assets/svgs/common/check.svg";
import SearchIcon from "../../../../assets/svgs/common/search.svg";
import { Input } from "../../../../components/ui/input";
import { XIcon } from "lucide-react";
import { useLinkWithDmsCampaignAdminHttp } from "../../../../hooks/http/admin/campaigns/link-dms-campaign.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";
import useCampaignStore from "../../../../store/campaign.store";
import { useParams } from "react-router-dom";
import type { ICampaign } from "../../../../types/models/campaign.models.types";
import type { ICampaignMetrics } from "../../../../types/models/campaign-metrics.types";
import type IDMSCampaign from "../../../../hooks/http/admin/dms/get-dms-campaigns-list.admin.http";
import {
  DMSCampaignStatus,
  useGetDMSCampaignsListAdminHttp,
} from "../../../../hooks/http/admin/dms/get-dms-campaigns-list.admin.http";
import { DateTime } from "luxon";
import { useGetDMSCampaignByIdAdminHttp } from "../../../../hooks/http/admin/dms/get-dms-campaign-by-id.admin.http";

export const LinkWithDMSCampaignSliderAdminPartial: FunctionComponent = () => {
  const [campaigns, setCampaigns] = useState<Array<IDMSCampaign>>();
  const [search, setSearch] = useState("");
  const [searchData, setSearchData] = useState(campaigns);
  const [unlinking, setUnlinking] = useState(false);
  const [dmsCampaignDetails, setDmsCampaignDetails] = useState<IDMSCampaign>();

  const { toast } = useToast();
  const { campaignId } = useParams();

  const {
    execute: linkWithDmsCampaignHttp,
    fetching: fetchingLinkWithDmsCampaign,
  } = useLinkWithDmsCampaignAdminHttp();

  const {
    execute: getDMSCampaignsListHttp,
    fetching: fetchingDMSCampaignsList,
  } = useGetDMSCampaignsListAdminHttp();

  const { execute: getDMSCampaignByIdHttp, fetching: fetchingDMSCampaignById } =
    useGetDMSCampaignByIdAdminHttp();

  async function getDMSCampaignById(id: string) {
    const response = await getDMSCampaignByIdHttp({
      body: null,
      pathParams: { dmsCampaignId: id },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setDmsCampaignDetails(response.data.campaign);
  }

  async function getDMSCampaignsList() {
    const response = await getDMSCampaignsListHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaigns(response.data.campaigns);
    setSearchData(response.data.campaigns);
  }

  const { data: mainCampaign, setData: setMainCampaignData } =
    useCampaignStore();
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);

  // The link response serializes the campaign without calculated metrics.
  // Replacing the store with it wipes the numbers already on the page.
  function applyLinkedCampaign(updated: ICampaign) {
    const currentYoutube = (
      mainCampaign as ICampaign & {
        youtube?: { metrics: ICampaignMetrics | null } | null;
      }
    ).youtube;
    const updatedYoutube = (
      updated as ICampaign & {
        youtube?: { metrics: ICampaignMetrics | null } | null;
      }
    ).youtube;

    setMainCampaignData({
      ...mainCampaign,
      ...updated,
      metrics: updated.metrics ?? mainCampaign.metrics,
      ...(updatedYoutube
        ? {
            youtube: {
              ...updatedYoutube,
              metrics: updatedYoutube.metrics ?? currentYoutube?.metrics ?? null,
            },
          }
        : {}),
    } as ICampaign);
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim() === "") {
      setSearchData(campaigns);
    } else {
      setSearchData(
        campaigns?.filter(
          (campaign) =>
            campaign.name.toLowerCase().includes(value.toLowerCase()) ||
            campaign.campaignId.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  async function linkWithDmsCampaign() {
    if (!selectedCampaign) {
      return;
    }
    if (!campaignId) {
      return;
    }
    const response = await linkWithDmsCampaignHttp({
      body: {
        dmsCampaignId: selectedCampaign,
      },
      pathParams: { campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    applyLinkedCampaign(response.data.campaign);
    document.getElementById("closeSheet")?.click();
  }

  async function unlinkWithDmsCampaign() {
    if (!campaignId) {
      return;
    }
    setUnlinking(true);
    const response = await linkWithDmsCampaignHttp({
      body: {
        dmsCampaignId: null,
      },
      pathParams: { campaignId },
    });
    if (!response.success) {
      setUnlinking(false);
      return handleHttpError(response, toast);
    }
    applyLinkedCampaign(response.data.campaign);
    setUnlinking(false);
    setDmsCampaignDetails(undefined);
    // document.getElementById("closeSheet")?.click();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getDMSCampaignsList();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setSelectedCampaign(mainCampaign?.dmsCampaign?.campaignId || null);
    if (mainCampaign.dmsCampaign?.campaignId) {
      getDMSCampaignById(mainCampaign.dmsCampaign.campaignId);
    }
  }, [mainCampaign?.dmsCampaign?.campaignId]);

  if (fetchingDMSCampaignsList) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="relative flex flex-col gap-6 p-5 w-full">
        <div className="top-5 sticky flex flex-col gap-3 bg-background w-full">
          <SheetTitle className="flex flex-col items-start gap-2 w-full">
            <p className="text-2xl text-white">Link DMS Campaign</p>
          </SheetTitle>
          <div className="flex flex-col items-start px-4 py-3 border border-border rounded-lg w-full text-secondary text-sm">
            <p>Selected DMS Campaign:</p>
            {fetchingDMSCampaignById ? (
              <Spinner width={15} />
            ) : dmsCampaignDetails ? (
              <div className="flex justify-between items-center pt-2 w-full">
                <div className="flex flex-col text-white">
                  <p className="font-semibold">{dmsCampaignDetails?.name}</p>
                  <p className="text-secondary text-sm">
                    Campaign lead:{" "}
                    {dmsCampaignDetails?.campaignLead
                      ? dmsCampaignDetails.campaignLead
                      : "-"}
                  </p>
                  <p className="text-secondary text-sm">
                    Approved at:{" "}
                    {dmsCampaignDetails?.approvedAt
                      ? DateTime.fromISO(
                          dmsCampaignDetails.approvedAt
                        ).toLocaleString(DateTime.DATETIME_MED)
                      : "-"}
                  </p>
                </div>
                {dmsCampaignDetails?.campaignId ===
                mainCampaign.dmsCampaign.campaignId ? (
                  <Button
                    onClick={unlinkWithDmsCampaign}
                    disabled={
                      fetchingLinkWithDmsCampaign ||
                      dmsCampaignDetails.campaignStatus ===
                        DMSCampaignStatus.Complete
                    }
                    className="bg-tertiarybuttonbg border border-border w-20 h-10"
                  >
                    {fetchingLinkWithDmsCampaign && unlinking ? (
                      <Spinner width={15} />
                    ) : (
                      "Unlink"
                    )}
                  </Button>
                ) : (
                  ""
                )}
              </div>
            ) : dmsCampaignDetails === undefined && !fetchingDMSCampaignById ? (
              "-"
            ) : (
              ""
            )}
          </div>
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="bg-transparent border-none w-80 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name, or description"
            />

            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  setSearchData(campaigns);
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
        </div>
        <div className="flex flex-col divide-border overflow-y-scroll">
          {searchData?.map((campaign) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <div
              onClick={() => {
                if (
                  dmsCampaignDetails?.campaignStatus ===
                  DMSCampaignStatus.Complete
                ) {
                  return;
                }

                setSelectedCampaign(
                  selectedCampaign === campaign.campaignId
                    ? null
                    : campaign.campaignId
                );
              }}
              key={campaign.campaignId}
              className={`w-full rounded-lg gap-3 font-semibold text-white flex items-center justify-between px-5 py-3  cursor-pointer  ${
                selectedCampaign === campaign.campaignId
                  ? "bg-tertiarybuttonbg"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3">
                {selectedCampaign === campaign.campaignId ? (
                  <CheckIcon />
                ) : (
                  <div className="border border-border rounded-full w-6 h-6" />
                )}
                <div className="flex flex-col">
                  <p>{campaign.name}</p>
                  <p className="text-secondary text-sm">
                    Campaign lead:{" "}
                    {campaign.campaignLead ? campaign.campaignLead : "-"}
                  </p>
                  <p className="text-secondary text-sm">
                    Approved at:{" "}
                    {campaign.approvedAt
                      ? DateTime.fromISO(campaign.approvedAt).toLocaleString(
                          DateTime.DATETIME_MED
                        )
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SheetFooter className="bottom-0 z-10 sticky bg-background p-5 border-t border-border w-full">
        <Button
          disabled={
            !selectedCampaign ||
            mainCampaign?.dmsCampaign?.campaignId === selectedCampaign ||
            dmsCampaignDetails?.campaignStatus === DMSCampaignStatus.Complete
          }
          onClick={linkWithDmsCampaign}
          className="bg-primarybuttonbg w-full h-14 font-semibold text-black"
        >
          {fetchingLinkWithDmsCampaign && !unlinking ? (
            <Spinner width={15} />
          ) : (
            "Save"
          )}
        </Button>
      </SheetFooter>
    </div>
  );
};
